<script setup lang="ts">
import { computed } from 'vue';
import type { ConnectionTool } from '../model.js';
import type { DirectoryDevice } from '../lib/directory.js';
import { formatRelativeTime, formatTimestamp, presenceLabel, presenceTone } from '../lib/format.js';
import AppIcon from './AppIcon.vue';
import DeviceGlyph from './DeviceGlyph.vue';
import OsIdentity from './OsIdentity.vue';
import MenuButton from './MenuButton.vue';
import MenuItem from './MenuItem.vue';
import StatusPill from './StatusPill.vue';

const props = defineProps<{
  device: DirectoryDevice;
  opening: boolean;
  showGroup: boolean;
  preferencesBusy?: boolean;
  removable?: boolean;
}>();
const emit = defineEmits<{
  select: [];
  open: [tool: ConnectionTool];
  customize: [];
  remove: [];
}>();

const meta = computed(() => {
  const device = props.device;
  const items: { text: string; title?: string }[] = [];
  if (device.source === 'saved') items.push({ text: 'SSH alias' });
  else if (device.presence === 'offline' && device.lastSeenAt)
    items.push({
      text: `Seen ${formatRelativeTime(device.lastSeenAt)}`,
      title: formatTimestamp(device.lastSeenAt),
    });
  if (props.showGroup) items.push({ text: device.group });
  return items;
});
const renamed = computed(() => props.device.name !== props.device.reportedName);
</script>

<template>
  <article class="device-card" :data-presence="props.device.presence">
    <div class="device-card-top">
      <DeviceGlyph
        :icon="props.device.icon"
        :color="props.device.color"
        :platform="props.device.reportedPlatform ?? props.device.platform"
        :os-version="props.device.osVersion"
        :presence="props.device.presence"
      />
      <div class="device-card-tools">
        <StatusPill :tone="presenceTone[props.device.presence]">
          {{ presenceLabel[props.device.presence] }}
        </StatusPill>
        <MenuButton :label="`More actions for ${props.device.name}`" small>
          <MenuItem icon="edit" :disabled="props.preferencesBusy" @click="emit('customize')"
            >Customize…</MenuItem
          >
          <MenuItem
            icon="devices"
            :disabled="!props.device.desktop.available || props.opening"
            :hint="props.device.desktop.explanation"
            @click="emit('open', 'desktop')"
          >
            Open desktop
          </MenuItem>
          <MenuItem
            v-if="!props.device.personalAccess"
            icon="terminal"
            :disabled="!props.device.terminal.available || props.opening"
            :hint="props.device.terminal.explanation"
            @click="emit('open', 'terminal')"
          >
            Open terminal
          </MenuItem>
          <MenuItem icon="chevronRight" @click="emit('select')">Device page</MenuItem>
          <MenuItem v-if="props.removable" icon="trash" @click="emit('remove')">Remove…</MenuItem>
        </MenuButton>
      </div>
    </div>
    <button
      type="button"
      class="device-title"
      :title="renamed ? `Reported as ${props.device.reportedName}` : undefined"
      @click="emit('select')"
    >
      <span class="device-title-text">
        <span class="device-name">{{ props.device.name }}</span>
        <span class="device-host">{{ props.device.hostname }}</span>
      </span>
      <AppIcon name="chevronRight" />
      <span class="sr-only">Connection details for {{ props.device.name }}</span>
    </button>
    <p class="device-meta">
      <OsIdentity
        :platform="props.device.reportedPlatform ?? props.device.platform"
        :version="props.device.osVersion"
        :fallback="props.device.source === 'saved' ? 'System checked when you connect' : undefined"
      />
      <span v-for="item in meta" :key="item.text" :title="item.title">{{ item.text }}</span>
    </p>
    <div class="device-card-actions">
      <span v-if="device.hostProfile === 'linux-server'" class="tag">Headless server</span>
      <button
        v-if="device.hostProfile !== 'linux-server'"
        type="button"
        :disabled="!props.device.desktop.available || props.opening"
        :title="props.device.desktop.explanation"
        @click="emit('open', 'desktop')"
      >
        <AppIcon name="devices" />Desktop<span class="sr-only"> on {{ props.device.name }}</span>
      </button>
      <button
        v-if="!props.device.personalAccess"
        type="button"
        :disabled="!props.device.terminal.available || props.opening"
        :title="props.device.terminal.explanation"
        @click="emit('open', 'terminal')"
      >
        <AppIcon name="terminal" />Terminal<span class="sr-only"> on {{ props.device.name }}</span>
      </button>
    </div>
  </article>
</template>
